/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, DoCheck, Input, OnDestroy, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {deferSignalStateWrite} from '../../util/template-state';

@Component({
  imports: [CommonModule],
  selector: 'adn-promise-spinner',
  template: `
    <p class="text-center" *ngIf="!displayResolved()">
      <span class="fa fa-2x fa-cog fa-spin"></span>
    </p>
    <div *ngIf="!andHide || displayResolved()" [attr.style]="style">
      <!-- *ngIf (not [hidden]): keep unresolved content out of CD to avoid NG0100. -->
      <section class="animate-fade" *ngIf="displayResolved()">
        <ng-content></ng-content>
      </section>
    </div>
  `
})
export class PromiseSpinnerComponent implements DoCheck, OnDestroy {
  /**
   * Preferred for resource queries: bind the query object (not query.$resolved)
   * so the parent expression stays stable when $resolved flips false→true mid-CD.
   */
  @Input()
  set query(value: any) {
    this.queryBound = true;
    this.queryValue = value;
  }

  get query(): any {
    return this.queryValue;
  }

  /** Flags / already-buffered booleans. Prefer [query] when watching $resolved. */
  @Input() resolved: any;
  @Input() andHide = false;
  @Input() style: string;

  readonly displayResolved = signal<any>(false);

  private queryBound = false;
  private queryValue: any;
  private lastQuery: any;
  private lastSeen: any;
  private initialized = false;
  private writeId = 0;
  private destroyed = false;

  ngDoCheck(): void {
    const source = this.queryBound ? this.queryValue?.$resolved : this.resolved;

    // New query object (refresh / first assign): apply sync to avoid a spinner flash
    // on already-resolved stubs and to hide content immediately on {$resolved:false}.
    if (this.queryBound && this.queryValue !== this.lastQuery) {
      this.writeId++;
      this.lastQuery = this.queryValue;
      this.lastSeen = source;
      this.displayResolved.set(source);
      this.initialized = true;
      return;
    }

    if (!this.initialized) {
      this.writeId++;
      this.initialized = true;
      this.lastSeen = source;
      this.displayResolved.set(source);
      return;
    }

    if (source === this.lastSeen) {
      return;
    }
    this.lastSeen = source;
    // Defer past checkNoChanges — parent CD often overlaps settle.
    const writeId = ++this.writeId;
    deferSignalStateWrite(() => {
      if (!this.destroyed && writeId === this.writeId) {
        this.displayResolved.set(source);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.writeId++;
  }
}
